// Add or edit a user. Partner-only, like the whole screen.
//
// ⚠ THE PASSWORD IS ASKED FOR ONLY WHEN CREATING. Changing somebody's name and resetting
// their credentials are different decisions, and a form that did both would do the second
// by accident — so editing has no password box at all, and resetting one is its own
// action from the row. The server matches: `PUT /admin/users/:id` cannot touch a hash.
import { useState } from 'react';
import he from '../../i18n/he';
import { usersAdminApi } from '../../api';
import Modal from '../../components/Modal';
import { TextInput, Select, ColorInput } from '../../components/Form';
import { ErrorText } from '../../components/Loader';
import { SELECTABLE_ROLES, roleLabel, ROLE } from './roles';

const MIN_PASSWORD = 8;

export default function UserModal({ user, onClose, onSaved }) {
  const isNew = !user?.id;
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || ROLE.SECRETARY,
    color: user?.color || '#2563eb',
    password: '',
  });
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const roleOptions = SELECTABLE_ROLES.map((r) => ({ value: r, label: roleLabel(r) }));

  // Save is blocked while a field is unfinished, and the note under it says which —
  // a dark button with no explanation is how a form teaches people to distrust it.
  const passwordMissing = isNew && form.password.length < MIN_PASSWORD;
  const incomplete = !form.name.trim() || !form.email.trim() || passwordMissing;

  const submit = async () => {
    try {
      const payload = {
        name: form.name,
        email: form.email,
        role: form.role,
        color: form.color,
      };
      if (isNew) await usersAdminApi.create({ ...payload, password: form.password });
      else await usersAdminApi.update(user.id, payload);
      onSaved();
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Modal
      title={isNew ? he.users.addTitle : he.users.editTitle}
      onClose={onClose}
      onSave={submit}
      saveDisabled={incomplete}
    >
      <TextInput label={he.users.name} value={form.name} onChange={set('name')} />
      {/* The server trims and lowercases this before storing it: the unique key on the
          email is case-sensitive, so two spellings would otherwise be two accounts for
          one person. */}
      <TextInput label={he.users.email} type="email" value={form.email} onChange={set('email')} />

      <Select label={he.users.role} value={form.role} onChange={set('role')} options={roleOptions} />

      {/* Presentation only — the calendar colours a person's meetings from it. */}
      <ColorInput label={he.users.color} value={form.color} onChange={set('color')} />

      {isNew && (
        <>
          <TextInput
            label={he.users.passwordNew}
            type="password"
            value={form.password}
            onChange={set('password')}
          />
          <p className="muted small-note">{he.users.passwordHint}</p>
        </>
      )}

      {error && <ErrorText error={error} />}
    </Modal>
  );
}
